import { Action, ActionName, CombinedTrigger, EventTrigger, EventType, ReplenishmentPlan } from '@ekkogmbh/apisdk';
import { Add, ArrowDownward, ArrowUpward, Delete, Edit, Settings } from '@mui/icons-material';
import { Box, Button, Card, CardContent, CardHeader, IconButton, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { CoordinateInput } from '../../Common/Components/CoordinateInput';
import { StyledFormHeader } from '../../Common/Components/Forms/StyledFormHeader';
import { EventTriggerForm } from 'src/EventRuleManagement/Components/EventTriggerForm';
import { ContentPanel, usePanel } from 'src/Common/Components/ContentControl/ContentPanel';
import { ReactiveRow } from 'src/Common/Components/Stacks';
import useEventTrigger, { getDefaultTrigger } from 'src/EventRuleManagement/Providers/EventTriggerProvider';
import useEkko from 'src/Common/Providers/EkkoProvider';
import useRequest from 'src/Common/Hooks/useRequest';
import usePagination from 'src/Common/Providers/PaginationProvider';

interface Props {
  closeHandler: () => void;
  replenishmentPlan?: ReplenishmentPlan;
}

export const ReplenishmentPlanPanel = ({ replenishmentPlan, closeHandler }: Props): React.JSX.Element => {
  const { api } = useEkko();
  const { enableSave } = usePanel();
  const { refresh } = usePagination();
  const { eventTrigger, setTriggers } = useEventTrigger();

  const [, startLoading] = useRequest();
  const [selectedStateIndex, setSelectedStateIndex] = useState<number | undefined>();
  const [nameInput, setNameInput] = useState<string | undefined>();

  const [name, setName] = useState<string>('');
  const [coordinate, setCoordinate] = useState<string>('');
  const [machineStates, setMachineStates] = useState<string[]>([]);
  const [machineTriggers, setMachineTriggers] = useState<Record<string, EventTrigger>>({});

  const savePayload = useMemo(() => {
    if ([name, coordinate].includes('') || machineStates.length === 0) {
      return undefined;
    }

    return {
      name,
      coordinate,
      linearStateMachineDefinition: {
        states: machineStates,
        triggers: machineTriggers,
        onEnterActions: machineStates.reduce(
          (carry, state) => {
            carry[state] = { name: ActionName.NOOP } as Action;
            return carry;
          },
          {} as Record<string, Action>,
        ),
      },
    } as ReplenishmentPlan;
  }, [name, coordinate, machineStates, machineTriggers]);

  const isEditOpen = useMemo(() => nameInput !== undefined, [nameInput]);

  const reset = useCallback(() => {
    setName(replenishmentPlan?.name ?? '');
    setCoordinate(replenishmentPlan?.coordinate ?? '');
    setMachineStates(replenishmentPlan?.linearStateMachineDefinition.states ?? []);
    setMachineTriggers(replenishmentPlan?.linearStateMachineDefinition.triggers ?? {});

    setTriggers(getDefaultTrigger(EventType.DEVICE_GEOFENCE));
    setNameInput(undefined);
    setSelectedStateIndex(undefined);
  }, [replenishmentPlan]);

  useEffect(() => {
    reset();
  }, [reset]);

  const onSave = useCallback(async () => {
    if (savePayload === undefined) {
      return;
    }

    startLoading(
      () => api.saveReplenishmentPlan(savePayload, replenishmentPlan !== undefined),
      () => {
        refresh();
        closeHandler();
      },
    );
  }, [closeHandler, api, savePayload, replenishmentPlan]);

  const addMachineState = useCallback(
    (name: string, trigger: EventTrigger) => {
      machineStates.push(name);
      machineTriggers[name] = trigger;

      setMachineStates([...machineStates]);
      setMachineTriggers({ ...machineTriggers });
    },
    [machineStates, machineTriggers],
  );

  const removeMachineState = useCallback(
    (name: string) => {
      const stateIndex = machineStates.indexOf(name);
      if (stateIndex >= 0) {
        machineStates.splice(stateIndex, 1);
        delete machineTriggers[name];

        setMachineStates([...machineStates]);
        setMachineTriggers({ ...machineTriggers });
      }
    },
    [machineStates, machineTriggers],
  );

  const replaceMachineState = useCallback(
    (index: number, name: string, trigger: EventTrigger) => {
      if (index < machineStates.length) {
        const removedName = machineStates.splice(index, 1, name)[0];
        delete machineTriggers[removedName];

        machineTriggers[name] = trigger;
        setMachineStates([...machineStates]);
        setMachineTriggers({ ...machineTriggers });
      }
    },
    [machineStates, machineTriggers],
  );

  const moveMachineState = useCallback(
    (from: number, to: number) => {
      const stateCount = machineStates.length;

      if (from !== to && from < stateCount && to < stateCount) {
        const movingName = machineStates.splice(from, 1)[0];
        machineStates.splice(to, 0, movingName);
        setMachineStates([...machineStates]);
      }
    },
    [machineStates],
  );

  const onCommitPending = useCallback(() => {
    if ([eventTrigger, nameInput].includes(undefined)) {
      return;
    }

    if (selectedStateIndex !== undefined) {
      replaceMachineState(selectedStateIndex, nameInput!, eventTrigger!);
    } else {
      addMachineState(nameInput!, eventTrigger!);
    }

    setSelectedStateIndex(undefined);
    setNameInput(undefined);
    setTriggers(undefined);
  }, [selectedStateIndex, nameInput, eventTrigger]);

  const onAddState = useCallback(() => {
    setTriggers(getDefaultTrigger(EventType.DEVICE_TRIGGER));
    setNameInput('');
    setSelectedStateIndex(undefined);
  }, []);

  const onEditState = useCallback(
    (name: string) => {
      const stateIndex = machineStates.indexOf(name);

      if (stateIndex < 0) {
        return;
      }
      setNameInput(name);
      setSelectedStateIndex(stateIndex);
      setTriggers(machineTriggers[name]);
    },
    [machineStates, machineTriggers],
  );

  /** on change selected state index */
  useEffect(() => {
    if (selectedStateIndex !== undefined && selectedStateIndex < machineStates.length) {
      const stateName = machineStates[selectedStateIndex];
      setNameInput(stateName);
      setTriggers(machineTriggers[stateName]);
    }
  }, [selectedStateIndex]);

  useEffect(() => {
    enableSave(savePayload !== undefined);
  }, [savePayload]);

  const StateBox = useCallback(
    ({ name, trigger, index }: { name: string; trigger: EventTrigger; index: number }) => {
      const maxIndex = machineStates.length - 1;
      return (
        <Box p={1}>
          <Card variant={'outlined'}>
            <CardHeader
              title={
                <Typography variant="h5" component="h2">
                  {name}
                </Typography>
              }
              action={
                <>
                  {index > 0 && (
                    <IconButton
                      disabled={isEditOpen}
                      aria-label="move-up"
                      onClick={() => {
                        moveMachineState(index, index - 1);
                      }}
                    >
                      <ArrowUpward />
                    </IconButton>
                  )}
                  {index < maxIndex && (
                    <IconButton
                      disabled={isEditOpen}
                      aria-label="move-down"
                      onClick={() => {
                        moveMachineState(index, index + 1);
                      }}
                    >
                      <ArrowDownward />
                    </IconButton>
                  )}
                  <IconButton disabled={isEditOpen} aria-label="delete" onClick={() => removeMachineState(name)}>
                    <Delete />
                  </IconButton>
                  <IconButton aria-label="edit" onClick={() => onEditState(name)}>
                    <Settings />
                  </IconButton>
                </>
              }
            />
            <CardContent>
              <Typography color="textSecondary" variant={'overline'}>
                Trigger
              </Typography>
              <Typography fontWeight={700}>{trigger.type}</Typography>
              <Box pl={4}>
                {Object.keys(trigger.properties).map((key) => {
                  if (trigger.type === 'combined') {
                    const combinedNames = (trigger as CombinedTrigger).properties.triggers.map(
                      (t: EventTrigger) => t.type,
                    );
                    return (
                      <Typography key={key} color={'textSecondary'}>
                        {combinedNames.join(', ')}
                      </Typography>
                    );
                  }
                  return (
                    <Typography key={key} color="textSecondary">
                      {key}: {trigger.properties[key]}
                    </Typography>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        </Box>
      );
    },
    [onEditState, isEditOpen, machineStates],
  );

  const stateStack = useMemo(
    () => (
      <Stack spacing={1}>
        <StyledFormHeader label={'States'} />
        {machineStates.map((name, index) => (
          <StateBox key={index} index={index} name={name} trigger={machineTriggers[name]} />
        ))}
        <Box alignItems={'center'} justifyContent={'center'} paddingLeft={2} paddingRight={2}>
          <Button fullWidth variant={'outlined'} color={'secondary'} onClick={onAddState}>
            <Add />
          </Button>
        </Box>
      </Stack>
    ),
    [StateBox, machineStates, machineTriggers],
  );

  const stateForm = useMemo(() => {
    const isEditState = selectedStateIndex !== undefined;

    return (
      <Stack spacing={2}>
        <StyledFormHeader label={(isEditState ? 'Edit' : 'Add') + ' State'} />
        <StyledTextField
          type="text"
          label="Name"
          value={nameInput}
          onChange={(e) => setNameInput(e.target.value as string)}
          autoFocus={true}
        />
        <div style={{ paddingLeft: 12 }}>
          <Typography color="textSecondary" variant={'overline'}>
            Triggers
          </Typography>
          <EventTriggerForm
            variant={'grid'}
            coordinate={coordinate}
            excludeTypes={[
              EventType.PICKING_ORDER_STARTED,
              EventType.PICKING_ORDER_CONCLUDED,
              EventType.EKANBAN_STATE_TRANSITION,
            ]}
          />
        </div>
        <Button
          variant="outlined"
          color="secondary"
          onClick={onCommitPending}
          disabled={eventTrigger === undefined || nameInput === ''}
          style={{ margin: 8 }}
        >
          {isEditState ? <Edit /> : <Add />} {isEditState ? 'Edit' : 'Add'} State
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={() => {
            setNameInput(undefined);
            setSelectedStateIndex(undefined);
          }}
          style={{ margin: 8 }}
        >
          Dismiss
        </Button>
      </Stack>
    );
  }, [nameInput, selectedStateIndex, onCommitPending, eventTrigger, coordinate]);

  const identiferForm = useMemo(() => {
    const isCompartmentFoundTriggered =
      Object.values(machineTriggers).find((t) => t.type === EventType.COMPARTMENT_FOUND) !== undefined;
    const coordinateSelectDisabled = replenishmentPlan !== undefined || isCompartmentFoundTriggered;
    return (
      <Stack spacing={2}>
        <StyledFormHeader label={'Identifier'} />
        <StyledTextField
          type="text"
          label="Name"
          value={name}
          disabled={replenishmentPlan !== undefined}
          onChange={(e) => setName(String(e.target.value))}
        />
        <CoordinateInput
          value={coordinate}
          disabled={coordinateSelectDisabled}
          onChange={(coordinate) => setCoordinate(coordinate)}
          trailingDelimiter={false}
        />
      </Stack>
    );
  }, [replenishmentPlan, name, coordinate, machineTriggers]);

  return (
    <ContentPanel
      steps={[
        {
          title: 'Replenishment Plan',
          contentCallback: () => (
            <ReactiveRow spacing={2}>
              {identiferForm}
              {stateStack}
              {isEditOpen && stateForm}
            </ReactiveRow>
          ),
        },
      ]}
      onSave={onSave}
      onReset={reset}
      onCancel={() => {
        reset();
        closeHandler();
      }}
    />
  );
};
